import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import TabsList from "../../../components/Common/TabsList"
import { handleError, loadImageFile, userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { addBlock, onDragEnd, readSettingsNavigations, removeBlock, SettingsNavProps } from "../../../api/navigation"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Container, HStack, Text, Box, Accordion, Icon, Button, Flex } from "@chakra-ui/react"
import { closestCenter, DndContext } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import useCustomToast from "../../../hooks/useCustomToast"
import { ApiError, MediaService, SettingsNavigationService, SettingsNavigationsUpdate } from "../../../client"
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form"
import { SortableItem } from "../../../components/Common/SortableItem"
import { FiMenu, FiPlusCircle, FiTrash2 } from "react-icons/fi"
import { readCategories } from "../../../api/categories"
import { Tooltip } from "../../../components/UI/tooltip"
import SettingsNavPoints from "../../../components/Navigation/SettingsNavPoints"
import { waitForCelerySuccess } from "../../../utils"

export const Route = createFileRoute("/_layout/settings/navigation")({
  component: Navigation,
})


function NavigationTable() {
    // Permission check
    const [nv_read, nv_update, me_create, me_read, ct_read] = useMemo(() => {
        const [nv_read, nv_update] = ["read", "update"].map((action) => userHasPermission([{ perm: `settings_navigation_${action}`, is_allowed: false }])[0].is_allowed)
        const [media_create, me_read] = ["create", "read"].map((action) => userHasPermission([{ perm: `media_${action}`, is_allowed: false }])[0].is_allowed)
        const [ct_read] = ["read"].map((action) => userHasPermission([{ perm: `categories_${action}`, is_allowed: false }])[0].is_allowed)

        return [nv_read, nv_update, media_create, me_read, ct_read]
    }, [userHasPermission])


    if(!nv_read) return <NoPermission/>

    // Get all categories
    const categoriesQuery = useMemo(() => readCategories({per_page: 0, page: 0, showInactive: false}), [])
    const { data: categoriesList } = useQuery(categoriesQuery)

    // Query
    const queryClient = useQueryClient()
    const showToast = useCustomToast()

    const {data: navigation} = useQuery({
        ...readSettingsNavigations(),
    })

    //Form useForm hook
    const defaultValues =  useMemo(() => ({
        navs: (navigation?.data ?? []).map(item => ({
            title: item.title,
            path: item.path,
            custom_view: item.custom_view ?? null,
            is_active: item.is_active ?? true,
            order_number: item.order_number,
            parent_order_number: item.parent_order_number,
            fk_media_id: item.fk_media?.id ?? null,
            fk_category_id: item.fk_category.id,
            file: null,
            file_path: item.fk_media?.path ?? null,
            id: item.id
        }))
    }), [navigation])

    const form = useForm<SettingsNavProps>({
        mode: "onBlur",
        criteriaMode: "all",
        defaultValues: defaultValues,
    })

    const {
        handleSubmit,
        reset,
        setValue,
        watch,
        control,
        formState: {isSubmitting, errors, isDirty},
    } = form

    const { insert } = useFieldArray({
        control,
        name: "navs"
    })

    const navs = watch("navs") ?? []

    useEffect(() => {
        if (!navigation) return
        reset(defaultValues)
    }, [defaultValues, reset])

    useEffect(() => {
        if (!navigation?.data) return

        navigation.data.map(async (navPoint, i) => {
            if(navPoint.fk_media) {
                const file = await loadImageFile(navPoint.fk_media.path, navPoint.fk_media.file_name)
                if(file) {
                    setValue(`navs.${i}.file`, file, { shouldDirty: false })
                    setValue(`navs.${i}.file_path`, navPoint.fk_media.path, { shouldDirty: false })
                }
            }
        })
    }, [navigation])

    // Mutate form data
    const mutation = useMutation({
        mutationFn: (data: SettingsNavigationsUpdate) =>
            SettingsNavigationService.updateSettingsNavigations({ requestBody: data }),
        onSuccess: () => {
            showToast("Aktion erfolgreich!", "Navigation wurde angepasst", "success")
        },
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["settings-navigation"] })
        },
    })

    // Mutate file
    const mutationMedia = useMutation({
        mutationFn: (data: File) =>
            MediaService.createMedia({ formData: { file: data } }),
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["media"] })
        },
    })

    // Submit form
    const onSubmit: SubmitHandler<SettingsNavProps> = async (data) => {
        // Handle media upload
        for (const nav of data.navs ?? []) {
            if (nav.file && !nav.file_path) {
                const media = await mutationMedia.mutateAsync(nav.file)
                nav.fk_media_id = media.id

                if (media.task_id) await waitForCelerySuccess(media.task_id)
            } else if (!nav.file && !nav.file_path) {
                nav.fk_media_id = null
            }
        }

        // remove file, file_path and id
        const cleaned = {
            navs: data.navs?.map(({ file, file_path, id, ...rest }) => rest)
        }

        mutation.mutate(cleaned)
    }

    // Cancle form
    const onCancel = () => {
        navigation?.data?.map(async (navPoint, i) => {
            if(navPoint.fk_media) {
                const file = await loadImageFile(navPoint.fk_media.path, navPoint.fk_media.file_name)
                if(file) {
                    setValue(`navs.${i}.file`, file, { shouldDirty: false })
                    setValue(`navs.${i}.file_path`, navPoint.fk_media.path, { shouldDirty: false })
                }
            }
        })

        reset(defaultValues)
    }

    const [openItems, setOpenItems] = useState<string[]>([])

    return (
        <Container maxW="full" my={10}>
            <HStack pb={4}>
                <Text>Navigation</Text>
            </HStack>

            <Box
                w={{ sm: "full", md: "50%" }}
                as="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Box border="1px solid" borderColor="dim" borderRadius={8}>
                    <DndContext collisionDetection={closestCenter} onDragEnd={(e) => onDragEnd(e, form)} onDragStart={() => setOpenItems([])}>
                        <SortableContext items={navs?.map(b => b.id)} strategy={verticalListSortingStrategy}>
                            <Accordion.Root multiple={true} value={openItems} onValueChange={(details) => setOpenItems(details.value)}>
                                {navs.filter(b => (b.parent_order_number ?? 0) == 0).map((block) => (
                                    <SortableItem id={block.id} key={block.id}>
                                        {({ listeners }) => (
                                            <Accordion.Item
                                                value={block.id}
                                                px={2}
                                                py={2}
                                                divideY="1px"
                                                divideColor="main"
                                            >
                                                <Accordion.ItemTrigger h="2.5em">
                                                    <Icon as={FiMenu} {...listeners} data-testid="drag_nav" _active={{cursor: "grabbing"}} cursor="pointer" />

                                                    <Box flex="1" textAlign="left" color={errors.navs?.length != undefined ? "danger" : undefined} data-testid="title_nav">
                                                        {block.title && block.title != "" ? block.title :  "Neuer Navigationspunkt"}

                                                        <Accordion.ItemIndicator display="none" />
                                                    </Box>

                                                    <Icon
                                                        as={FiTrash2}
                                                        color="danger"
                                                        onClick={(e) => { e.stopPropagation(), removeBlock(block.id, form) }}
                                                        data-testid="delete_nav"
                                                        cursor="pointer"
                                                    />
                                                </Accordion.ItemTrigger>

                                                <Accordion.ItemContent pt={4} borderRadius="none">
                                                    <Accordion.ItemBody>
                                                        <SettingsNavPoints
                                                            block_num={navs.indexOf(block)}
                                                            me_create={me_create}
                                                            me_read={me_read}
                                                            ct_read={ct_read}
                                                            categories_list={categoriesList}
                                                            blocks={navs}
                                                            insert={insert}
                                                            form={form}
                                                            openItems={openItems}
                                                            setOpenItems={setOpenItems}
                                                        />

                                                    </Accordion.ItemBody>
                                                </Accordion.ItemContent>
                                            </Accordion.Item>
                                        )}
                                    </SortableItem>
                                ))}
                            </Accordion.Root>
                        </SortableContext>
                    </DndContext>

                    <Tooltip showArrow content="Navigationspunkt hinzufügen" openDelay={500}>
                        <Box>
                            <Button
                                onClick={() => addBlock(null, insert, form, setOpenItems)}
                                w="100%"
                                h="fit-content"
                                py={2}
                                backgroundColor="transparent"
                                color="main"
                                data-testid="add_nav"
                            >
                                <Icon as={FiPlusCircle} boxSize="25px" />
                            </Button>
                        </Box>
                    </Tooltip>
                </Box>

                <Flex mt={4} mb={8} gap={8}>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isDirty || !nv_update}
                    >
                        Speichern
                    </Button>

                    <Button
                        onClick={onCancel}
                        disabled={isSubmitting || !isDirty}
                        variant="secondary"
                    >
                        Abbrechen
                    </Button>
                </Flex>
            </Box>
        </Container>
    )
}

export default NavigationTable

function Navigation() {
        const tabs = useMemo(() => [
        { value: "settings", title: "Allgemein" },
        { value: "settings/navigation", title: "Navigation" },
        { value: "settings/landingpage", title: "Startseite" },
        { value: "settings/mimetypes", title: "Dateitypen" },
        { value: "settings/subscriptions", title: "Abos" },
        { value: "settings/priocategories", title: "Kategorien" },
        { value: "settings/ratecharts", title: "Tariftabellen" },
    ], [])

    return <TabsList tabs={tabs} defaultVal="settings" />
}